import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Flex,
  Grid,
  Group,
  Menu,
  Modal,
  Paper,
  Popover,
  ScrollArea,
  Select,
  Skeleton,
  Stepper,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconFile,
  IconFileInfo,
  IconListCheck,
  IconListDetails,
  IconMapPin,
  IconMedicineSyrup,
  IconMessage,
  IconTableExport,
  IconTrash,
  IconX,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TimeInput } from '@mantine/dates';
import { ReceiptUserListItem } from '../../@types/daily-work';
import { EmployeeTransportUserListItem } from '../../@types/employee-transport';
import { CustomCalendar } from '../../components/custom-calendar';
import { CardFile } from '../../components/custom-card-file';
import { CustomDatePicker } from '../../components/custom-date-picker';
import { CustomDateTimePicker } from '../../components/custom-date-time-picker';
import { CustomLoader } from '../../components/custom-loader';
import { CustomMultipleFileButton } from '../../components/custom-multiple-file-input';
import { Page } from '../../components/page';
import {
  getDailyWorkReceiptUserListRequest,
  getDailyWorksRequest,
  setDailyWorkBankHourRequest,
} from '../../data/daily-work';
import { listEmployeeTransportUsersRequest } from '../../data/employee-transport';
import { useCheckBankHoursToUse } from '../../data/hooks/bank-hours';
import { RecalculateModal } from '../../components/recalculate-modal';
import {
  useAddDailyWorkJustification,
  useAddDailyWorkManually,
  useAddDailyWorkPartialJustification,
  useEditDailyWork,
  useEditDailyWorkJustification,
  useGenerateDailyWorkReceipt,
  useGetDailyWorkFiles,
  useRemoveJustification,
} from '../../data/hooks/daily-work';
import { useGenerateEmployeeTransportReceipt } from '../../data/hooks/employee-transport';
import { useUploadMultipleFiles } from '../../data/hooks/files';
import {
  useGenerateUnifiedReceipt,
  usePreviewUnifiedReceipt,
} from '../../data/hooks/unified-receipt';
import { PreviewUnifiedReceiptResponse } from '../../data/unified-receipts';
import { getUsersRequest } from '../../data/user';
import { useAuth } from '../../hooks/useAuth';
import { DailyWorkResponse, WorkType } from '../../models/daily-work';
import { exportDailyWorkToExcel } from '../../utils/export-daily-work-excel';
import { User, UserRole, UserType } from '../../models/user';
import { dayjsPlugins, formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import {
  dailyWorkJustificationHumanized,
  dailyWorkTypeColor,
  dailyWorkTypeHumanized,
  getFaultLabel,
  workJustification,
} from '../../utils/constants/daily-work';
import { userRoleHumanized, userRoleList } from '../../utils/constants/user';
import {
  formatBRL,
  formatDateTime,
  formatHours,
  realToUSCash,
} from '../../utils/helpers';
import { useDebouncedValue } from '@mantine/hooks';

type EditDailyWorkFormType = {
  begin: Date;
  start_lounch?: Date;
  end_lounch?: Date;
  finish?: Date;
  description?: string;
  isPaid: boolean;
  price: string;
};

type CreateDailyWorkFormType = {
  user: string;
  begin: Date;
  start_lounch?: Date;
  end_lounch?: Date;
  finish?: Date;
  description?: string;
};

type AddDailyWorkJustificationFormType = {
  user: string;
  justification: string;
  files: File[];
};

type EditDailyWorkJustificationFormType = {
  user: string;
  begin: Date;
  justification: string;
};

type ParcialDailyWorkJustificationFormType = {
  justification: string;
  start: Date | undefined;
  end: Date | undefined;
  files: File[];
};

type UserToVT = { id: number; value: string };

type PageModalState =
  | 'confirm-receipt'
  | 'generate-receipt'
  | 'generate-vt'
  | 'edit-daily-work'
  | 'add-daily-work'
  | 'add-work-justification'
  | 'edit-work-justification'
  | 'partial-justification'
  | 'generate-receipt-unified'
  | 'generate-receipt-unified-pdf'
  | 'view-files'
  | null;

type ModalConfirmationState = 'confirm-create' | null;

function downloadFile(response: Blob, fileName: string) {
  const url = URL.createObjectURL(new Blob([response]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
}

function DateFormInput({
  form,
  formKey,
  label,
}: {
  form: UseFormReturnType<any>;
  formKey: keyof Pick<
    CreateDailyWorkFormType | EditDailyWorkFormType,
    'begin' | 'start_lounch' | 'end_lounch' | 'finish'
  >;
  label: string;
}) {
  return (
    <>
      {form.values[formKey] === undefined ? (
        <>
          <Text>{label}</Text>
          <Button
            variant="subtle"
            onClick={() => form.setFieldValue(formKey, new Date())}
          >
            {form.values?.[formKey]
              ? formatDateTime(form.values[formKey] ?? '')
              : '--'}
          </Button>
        </>
      ) : (
        <Flex>
          <CustomDateTimePicker
            label={label}
            date={form.values[formKey] ?? new Date()}
            onChange={(dateTime) => form.setFieldValue(formKey, dateTime)}
          />
          <Button
            color="red"
            m={0}
            p={4}
            variant="subtle"
            onClick={() => form.setFieldValue(formKey, undefined)}
          >
            <IconX size={16} />
          </Button>
        </Flex>
      )}
    </>
  );
}

export function DailyWorkList() {
  const { user } = useAuth();
  const [pageLoading, setPageLoading] = useState(false);
  const [pageTotal, setPageTotal] = useState(0);
  const [unifiedReceiptStep, setUnifiedReceiptStep] = useState<
    'selection' | 'preview'
  >('selection');
  const [previewUnifiedData, setPreviewUnifiedData] =
    useState<PreviewUnifiedReceiptResponse | null>(null);

  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [modalConfirmation, setPageModalConfirmation] =
    useState<ModalConfirmationState>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [etUserList, setEtUserList] = useState<EmployeeTransportUserListItem[]>(
    [],
  );
  const [receiptUserList, setReceiptUserList] = useState<ReceiptUserListItem[]>(
    [],
  );
  const [selectReceiptUser, setSelectReceiptUser] = useState<
    ReceiptUserListItem[]
  >([]);
  const [selectedUsersToReceipt, setSelectedUsersToReceipt] = useState<
    number[]
  >([]);

  const [selectedUsersToVT, setSelectedUsersToVT] = useState<UserToVT[]>([]);
  const [receiptStep, setReceiptStep] = useState(0);
  const [selectedReceiptOptions, setSelectedReceiptOptions] = useState<
    string[]
  >([]);
  const maxSteps = selectedReceiptOptions.length === 2 ? 3 : 2;

  const nextStep = () => {
    setReceiptStep((current) => Math.min(current + 1, 3));

    if (!selectedReceiptOptions.includes('transport')) return;

    handleGetETUsers({ role: receiptListForm.values.role });
    transportListForm.setValues({ role: receiptListForm.values.role });
  };

  const prevStep = () => {
    if (receiptStep === 1) {
      setSelectedUsersToVT([]);
      setUnifiedReceiptStep('selection');
      setPreviewUnifiedData(null);
      setPreviewPage(1);
      setVtModalPage(1);
      setSelectedUsersToReceipt([]);
      setSelectedReceiptOptions([]);
      setSelectedDates([]);
      receiptListForm.reset();
      transportListForm.reset();
    }
    setReceiptStep((current) => Math.max(current - 1, 0));
  };

  const {
    fetch: addDailyWorkManuallyFetcher,
    loading: addDailyWorkManuallyLoader,
  } = useAddDailyWorkManually();

  const { fetch: editDailyWorkFetcher, loading: editDailyWorkLoader } =
    useEditDailyWork();

  const {
    fetch: addDailyWorkJustificationFetcher,
    loading: addDailyWorkJustificationLoader,
  } = useAddDailyWorkJustification();

  const {
    fetch: editDailyWorkJustificationFetcher,
    loading: editDailyWorkJustificationLoader,
  } = useEditDailyWorkJustification();

  const {
    fetch: addDailyWorkPartialJustificationFetcher,
    loading: addDailyWorkPartialJustificationLoader,
  } = useAddDailyWorkPartialJustification();

  const {
    fetch: checkBankHoursToUseFetcher,
    loading: checkBankHoursToUseLoader,
    response: checkBankHoursToUseResponse,
  } = useCheckBankHoursToUse();

  const {
    response: generateDailyWorkReceiptResponse,
    fetch: generateDailyWorkReceiptFetcher,
    loading: generateDailyWorkReceiptLoader,
  } = useGenerateDailyWorkReceipt();

  const {
    response: generateEmployeeTransportReceiptResponse,
    fetch: generateEmployeeTransportReceiptFetcher,
    loading: generateEmployeeTransportReceiptLoader,
  } = useGenerateEmployeeTransportReceipt();

  const {
    fetch: generateUnifiedReceiptFetcher,
    loading: generateUnifiedReceiptLoader,
  } = useGenerateUnifiedReceipt();

  const {
    fetch: previewUnifiedReceiptFetcher,
    loading: previewUnifiedReceiptLoader,
  } = usePreviewUnifiedReceipt();

  const {
    fetch: removeJustificationFetcher,
    loading: removeJustificationLoader,
  } = useRemoveJustification();

  const {
    response: getDailyWorkFilesResponse,
    fetch: getDailyWorkFilesFetcher,
    loading: getDailyWorkFilesLoader,
  } = useGetDailyWorkFiles();

  const { fetch: uploadFilesFetcher, loading: uploadFilesLoader } =
    useUploadMultipleFiles();

  const [globalUnifiedRole, setGlobalUnifiedRole] = useState<string | null>(
    UserRole.DAILY_EMPLOYEE,
  );
  const [globalUnifiedName, setGlobalUnifiedName] = useState<string>('');
  const [debouncedUnifiedName] = useDebouncedValue(globalUnifiedName, 300);
  const [vtModalPage, setVtModalPage] = useState(1);
  const vtModalPageSize = 50;
  const [previewPage, setPreviewPage] = useState(1);
  const previewPageSize = 15;
  const [userSearch, setUserSearch] = useState<string>('');
  const [debouncedUserSearch] = useDebouncedValue(userSearch, 300);
  const [userList, setUserList] = useState<User[]>([]);
  const [dailyWorkList, setDailyWorkList] = useState<DailyWorkResponse[]>([]);
  const [selectedDailyWork, setSelectedDailyWork] =
    useState<DailyWorkResponse>();
  const [selectedDailyWorkList, setSelectedDailyWorkList] = useState<
    DailyWorkResponse[]
  >([]);

  const isManagerViewer = user?.user.role === UserRole.MANAGER_VIEWER;
  // same rule of the server: master, or manager with creator role
  const canRecalculate =
    user?.user.type === UserType.MASTER ||
    (user?.user.type === UserType.MANAGER &&
      user?.user.role === UserRole.MANAGER_CREATOR);
  const [recalculating, setRecalculating] = useState(false);
  const isNotMaster = user?.user.type !== UserType.MASTER;

  const formFilter = useForm({
    initialValues: {
      userId: '',
      dateBegin: new Date(),
      dateEnd: new Date(),
      role: '',
    },
  });

  const receiptListForm = useForm({
    initialValues: {
      dateBegin: new Date(),
      dateEnd: new Date(),
      role: 'daily-employee',
    },
  });

  const transportListForm = useForm({
    initialValues: {
      role: 'daily-employee',
    },
  });

  const editDailyWorkForm = useForm<EditDailyWorkFormType>({
    initialValues: {
      begin: new Date(),
      start_lounch: undefined,
      end_lounch: undefined,
      finish: undefined,
      description: '',
      isPaid: false,
      price: '',
    },
  });

  const addDailyWorkForm = useForm<CreateDailyWorkFormType>({
    initialValues: {
      user: '',
      begin: new Date(),
      start_lounch: undefined,
      end_lounch: undefined,
      finish: undefined,
      description: '',
    },
  });

  const addDailyWorkJustificationForm =
    useForm<AddDailyWorkJustificationFormType>({
      initialValues: {
        user: '',
        justification: '',
        files: [],
      },
    });

  const editDailyWorkJustificationForm =
    useForm<EditDailyWorkJustificationFormType>({
      initialValues: {
        user: '',
        begin: new Date(),
        justification: '',
      },
    });

  const parcialDailyWorkJustificationForm =
    useForm<ParcialDailyWorkJustificationFormType>({
      initialValues: {
        start: undefined,
        end: undefined,
        justification: '',
        files: [],
      },
    });

  const handleFilter = useCallback(async (values: typeof formFilter.values) => {
    try {
      setPageLoading(true);
      const response = await getDailyWorksRequest({
        dateBegin: values.dateBegin.toISOString(),
        dateEnd: values.dateEnd.toISOString(),
        user: values.userId ? Number(values.userId) : undefined,
        role: values.role ? (values.role as UserRole) : undefined,
      });

      setDailyWorkList(response);
      setPageLoading(false);

      const sum = response.reduce((acc, work) => {
        if (work.type === WorkType.WORK && work.totalValue) {
          const numStr = work.totalValue
            .replace(/[R$\s]/g, '')
            .replace(/\./g, '')
            .replace(',', '.')
            .trim();
          const num = Number(numStr);
          if (!isNaN(num)) return acc + num;
        }
        return acc;
      }, 0);
      setPageTotal(sum);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar horários.',
        message: 'tente novamente',
      });
    }
  }, []);

  function handleSelectUserToVT(userToVT: UserToVT) {
    const listCopy = selectedUsersToVT;
    const findIndex = selectedUsersToVT.findIndex(
      (item) => item.id === userToVT.id,
    );
    if (findIndex !== -1) {
      listCopy.splice(findIndex, 1);
      setSelectedUsersToVT([...listCopy]);
    } else {
      setSelectedUsersToVT([...selectedUsersToVT, userToVT]);
    }
  }

  function handleSelectDailyWork(dailyWork: DailyWorkResponse) {
    setSelectedDailyWork(dailyWork);
    editDailyWorkForm.setValues({
      begin: dailyWork.begin
        ? dayjsPlugins(dailyWork.begin).toDate()
        : undefined,
      start_lounch: dailyWork.start_lounch
        ? dayjsPlugins(dailyWork.start_lounch).toDate()
        : undefined,
      end_lounch: dailyWork.end_lounch
        ? dayjsPlugins(dailyWork.end_lounch).toDate()
        : undefined,
      finish: dailyWork.finish
        ? dayjsPlugins(dailyWork.finish).toDate()
        : undefined,
      description: dailyWork.description,
      isPaid: dailyWork.isPaid,
      price: formatBRL(dailyWork.price),
    });
    setPageModal('edit-daily-work');
  }

  function handleSelectJustification(dailyWork: DailyWorkResponse) {
    setSelectedDailyWork(dailyWork);
    editDailyWorkJustificationForm.setValues({
      user: dailyWork.username,
      begin: dayjsPlugins(dailyWork.begin).toDate(),
      justification: dailyWork.justification,
    });
    setPageModal('edit-work-justification');
  }

  function handleSelectDates(dates: Date[]) {
    setSelectedDates(dates);
  }

  function onCloseDailyReceiptModal() {
    setPageModal(null);
    setReceiptUserList([]);
    setSelectReceiptUser([]);
    receiptListForm.reset();
  }

  function handleSelectUserToReceipt(id: number) {
    const listCopy = selectedUsersToReceipt;
    const findIndex = selectedUsersToReceipt.findIndex((item) => item === id);
    if (findIndex !== -1) {
      listCopy.splice(findIndex, 1);
      setSelectedUsersToReceipt([...listCopy]);
    } else {
      setSelectedUsersToReceipt([...selectedUsersToReceipt, id]);
    }
  }

  async function handleEditDailyWork(values: typeof editDailyWorkForm.values) {
    if (!selectedDailyWork) return;

    editDailyWorkFetcher({
      id: selectedDailyWork?.id,
      data: {
        description: values.description ?? undefined,
        isPaid: values.isPaid,
        price: realToUSCash(values?.price) ?? '0',
        start_lounch: values.start_lounch ?? null,
        end_lounch: values.end_lounch ?? null,
        begin: values.begin ?? null,
        finish: values.finish ?? null,
      },
      onSuccess: () => {
        handleFilter(formFilter.values);
        editDailyWorkForm.reset;
        setPageModal(null);
        successNotification({
          title: 'Horário Atualizado.',
          message: 'tudo certo!',
        });
      },
    });
  }

  async function handleSubmitSetBankHour() {
    if (!selectedDailyWorkList.length) {
      return;
    }

    try {
      setPageLoading(true);
      await setDailyWorkBankHourRequest({
        ids: selectedDailyWorkList.map(({ id }) => id),
      });
      setPageLoading(false);
      setPageModal(null);
      setSelectedDailyWorkList([]);
      handleFilter(formFilter.values);
      successNotification({
        title: 'Usuários adicionados a escala.',
        message: 'tudo certo',
      });
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao adicionar usuário a escala.',
        message: 'tente novamente',
      });
    }
  }

  async function handleDailyWorkReceitpExport(
    values: typeof receiptListForm.values,
  ) {
    if (!selectedUsersToReceipt.length) {
      errorNotification({
        title: 'Erro ao gerar recibo de Diária.',
        message: 'necessário selecionar ao menos um usuário.',
      });
      return;
    }

    await generateDailyWorkReceiptFetcher({
      query: {
        dateBegin: values.dateBegin.toISOString(),
        dateEnd: values.dateEnd.toISOString(),
        users: selectedUsersToReceipt,
      },
      onSuccess: (response: Blob) => {
        downloadFile(
          response,
          `recibo-diaria-${String(new Date(Date.now()).valueOf()).slice(
            7,
            11,
          )}.pdf`,
        );
        setPageLoading(false);
        setSelectedUsersToReceipt([]);
        setReceiptUserList([]);
        receiptListForm.reset();
      },
    });
  }

  async function handleGetETUsers(values: typeof transportListForm.values) {
    try {
      setPageLoading(true);
      const response = await listEmployeeTransportUsersRequest({
        role: values.role as UserRole,
      });
      setEtUserList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  async function handleEmployeeTransportReceitpExport() {
    const formatedDates = selectedDates.map((date) => date.toISOString());
    const userList = selectedUsersToVT.map((item) => ({
      days: formatedDates,
      rechargeValue: item.value,
      user: { id: item.id },
    }));

    if (!userList.length) {
      errorNotification({
        title: 'Erro ao gerar recibo de VT.',
        message: 'necessário selecionar ao menos um usuário.',
      });
      return;
    }

    await generateEmployeeTransportReceiptFetcher({
      data: {
        items: userList,
      },
      onSuccess: (response: Blob) => {
        downloadFile(
          response,
          `recibo-vt-${String(new Date(Date.now()).valueOf()).slice(
            7,
            11,
          )}.pdf`,
        );
        setPageLoading(false);
        setPageModal('confirm-receipt');
        setSelectedDates([]);
        setSelectedUsersToVT([]);
        transportListForm.reset();
      },
    });
  }

  async function handleCloseVTModal() {
    setPageModal(null);
    setSelectedUsersToVT([]);
    setSelectedDates([]);
  }

  async function getUsers(username?: string) {
    if (!username) {
      setUserList([]);
      return;
    }

    try {
      const response = await getUsersRequest({ username });
      setUserList(response);
    } catch (error) {
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  async function handleFilterReceiptList({
    dateBegin,
    dateEnd,
    role,
  }: typeof receiptListForm.values) {
    try {
      setPageLoading(true);
      const response = await getDailyWorkReceiptUserListRequest({
        dateBegin: dateBegin.toISOString(),
        dateEnd: dateEnd.toISOString(),
        role: role ? (role as UserRole) : undefined,
      });
      setReceiptUserList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  async function handleAddDailyWork(values: typeof addDailyWorkForm.values) {
    await addDailyWorkManuallyFetcher({
      data: {
        description: values.description,
        start_lounch: values.start_lounch,
        end_lounch: values.end_lounch,
        begin: values.begin,
        finish: values.finish,
        user: Number(values.user),
      },
      onSuccess: () => {
        addDailyWorkForm.reset();
        setPageModal(null);
        handleFilter(formFilter.values);
      },
    });
  }

  async function addDailyWordJustification(
    values: typeof addDailyWorkJustificationForm.values,
  ) {
    if (!selectedDates.length) {
      errorNotification({
        title: '',
        message: 'Selecione a data',
      });
      return;
    }

    const { files } = values;

    let fileIds: number[] = [];

    fileIds = await handleUploadFiles(files, 'Tente novamente.');

    // Function to order date array
    const formatedDates = selectedDates
      .sort(function (a, b) {
        return a.getTime() - b.getTime();
      })
      .map((date) => date.toISOString());

    await addDailyWorkJustificationFetcher({
      data: {
        date: formatedDates,
        user: Number(values.user),
        justification: values.justification,
        files: fileIds.map((fileId) => fileId),
      },
      onSuccess: () => {
        addDailyWorkJustificationForm.reset();
        setSelectedDates([]);
        setPageModal(null);
        successNotification({
          title: 'Justificativa adicionada',
          message: 'tudo certo.',
        });
        handleFilter(formFilter.values);
      },
    });
  }

  async function editDailyWordJustification(
    values: typeof editDailyWorkJustificationForm.values,
  ) {
    if (!selectedDailyWork) {
      return;
    }

    await editDailyWorkJustificationFetcher({
      id: selectedDailyWork.id,
      data: {
        begin: values.begin,
        justification: values.justification,
        user: values.user,
      },
      onSuccess: () => {
        editDailyWorkJustificationForm.reset();
        setPageModal(null);
        successNotification({
          title: 'Justificativa editada',
          message: 'tudo certo.',
        });
        handleFilter(formFilter.values);
      },
    });
  }

  async function handleCloseModal() {
    editDailyWorkJustificationForm.reset();
    setSelectedDates([]);
    setSelectedDailyWork(undefined);
    setPageModal(null);
    setSelectedUsersToVT([]);
    setSelectedUsersToReceipt([]);
    setSelectedReceiptOptions([]);
    setReceiptStep(0);
    setUnifiedReceiptStep('selection');
    setPreviewUnifiedData(null);
    setVtModalPage(1);
    setPreviewPage(1);
    receiptListForm.reset();
    transportListForm.reset();
    parcialDailyWorkJustificationForm.reset();
    setPageModalConfirmation(null);
  }

  async function generateReceiptsUnified() {
    await handleDailyWorkReceitpExport(receiptListForm.values);
    await handleEmployeeTransportReceitpExport();
    if (
      generateDailyWorkReceiptResponse ||
      generateEmployeeTransportReceiptResponse
    ) {
      handleCloseModal();
      setPageModal('confirm-receipt');
    }
  }

  async function handlePreviewUnifiedReceipt() {
    const formatedDates = selectedDates.map((date) => date.toISOString());
    const vtItems = selectedUsersToVT.map((item) => ({
      days: formatedDates,
      rechargeValue: item.value,
      user: { id: item.id },
    }));

    await previewUnifiedReceiptFetcher({
      data: {
        dailyWorks: {
          dateBegin: receiptListForm.values.dateBegin.toISOString(),
          dateEnd: receiptListForm.values.dateEnd.toISOString(),
          users: selectedUsersToReceipt,
        },
        transports: {
          items: vtItems,
        },
      },
      onSuccess: (response) => {
        setPreviewUnifiedData(response);
        setUnifiedReceiptStep('preview');
      },
    });
  }

  async function handleGenerateUnifiedPDF() {
    const formatedDates = selectedDates.map((date) => date.toISOString());
    const vtItems = selectedUsersToVT.map((item) => ({
      days: formatedDates,
      rechargeValue: item.value,
      user: { id: item.id },
    }));

    await generateUnifiedReceiptFetcher({
      data: {
        dailyWorks: {
          dateBegin: receiptListForm.values.dateBegin.toISOString(),
          dateEnd: receiptListForm.values.dateEnd.toISOString(),
          users: selectedUsersToReceipt,
        },
        transports: {
          items: vtItems,
        },
      },
      onSuccess: (response: Blob) => {
        downloadFile(
          response,
          `recibo-unificado-${String(new Date(Date.now()).valueOf()).slice(
            7,
            11,
          )}.pdf`,
        );
        handleCloseModal();
      },
    });
  }

  async function handleSelectPartialJustification(
    dailyWork: DailyWorkResponse,
  ) {
    if (!dailyWork) {
      return;
    }
    await checkBankHoursToUseFetcher({
      params: {
        users: dailyWork.user.toString(),
        interval: dailyWork.begin ? dailyWork.begin.toString() : '',
      },
    });
    setSelectedDailyWork(dailyWork);
    setPageModal('partial-justification');
  }

  const rangeTimeParcialJustification = useMemo(() => {
    const { start, end } = parcialDailyWorkJustificationForm.values;

    if (!start || !end) return 0;

    return dayjsPlugins(end).diff(start, 'minutes');
  }, [parcialDailyWorkJustificationForm.values]);

  async function handleAddPartialJustification(
    values: typeof parcialDailyWorkJustificationForm.values,
    isConfirmCreate?: boolean,
  ) {
    const { start, end, files } = parcialDailyWorkJustificationForm.values;

    if (!start || !end) {
      errorNotification({
        message: 'Tente novamente',
        title: 'Por favor, preencha os campos de horário',
      });
      return;
    }

    let fileIds: number[] = [];

    fileIds = await handleUploadFiles(files, 'Tente novamente.');

    if (!checkBankHoursToUseResponse?.[0].isAllowedToUse && !isConfirmCreate) {
      setPageModalConfirmation('confirm-create');
    } else {
      await addDailyWorkPartialJustificationFetcher({
        id: Number(selectedDailyWork?.id),
        data: {
          ...values,
          justificationTime: rangeTimeParcialJustification * 60,
          files: fileIds.map((fileId) => fileId),
        },
        onSuccess: () => {
          handleCloseModal();
          successNotification({
            title: 'Justificativa adicionada',
            message: 'tudo certo.',
          });
          handleFilter(formFilter.values);
        },
      });
    }
  }

  async function handleRemoveJustification(id: number) {
    await removeJustificationFetcher({
      id,
      onSuccess: () => {
        handleFilter(formFilter.values);
      },
    });
  }

  async function handleViewFiles(dailyWorkId: number) {
    await getDailyWorkFilesFetcher({
      id: dailyWorkId,
    });
    setPageModal('view-files');
  }

  async function handleUploadFiles(
    fileList: File[],
    errorMessage: string,
  ): Promise<number[]> {
    let fileIds: number[] = [];

    if (!fileList.length) return [];

    try {
      await uploadFilesFetcher({
        data: {
          files: fileList,
        },
        onSuccess: (res) => {
          fileIds = res;
        },
      });
      return fileIds;
    } catch (error) {
      errorNotification({
        title: 'Erro ao realizar upload dos arquivos',
        message: errorMessage,
      });
      return [];
    }
  }

  const RenderDataTable = useMemo(
    () => (
      <DataTable
        selectedRecords={selectedDailyWorkList}
        onSelectedRecordsChange={(dWork) => setSelectedDailyWorkList(dWork)}
        fetching={pageLoading}
        minHeight={500}
        noRecordsText="Sem horários"
        withBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={dailyWorkList}
        columns={[
          {
            accessor: 'type',
            title: '',
            render: ({ type, role }) => (
              <Badge color={dailyWorkTypeColor[type]}>
                {type === WorkType.FAULT
                  ? getFaultLabel(role)
                  : dailyWorkTypeHumanized[type]}
              </Badge>
            ),
          },
          {
            accessor: 'username',
            title: 'Funcionário',
          },
          {
            accessor: 'role',
            title: 'Tipo',
            render: ({ role }) =>
              role ? userRoleHumanized[role as UserRole] ?? role : '--',
          },
          {
            accessor: 'access_code',
            title: 'Código',
          },
          {
            accessor: 'begin',
            title: 'Entrada',
            render: ({ begin }) =>
              begin ? formatLocale(begin, 'DD/MM/YYYY HH:mm:ss') : '--',
          },
          {
            accessor: 'start_lounch',
            title: 'Inicio Intervalo',
            render: ({ start_lounch }) =>
              start_lounch
                ? formatLocale(start_lounch, 'DD/MM/YYYY HH:mm:ss')
                : '--',
          },
          {
            accessor: 'end_lounch',
            title: 'Fim Intervalo',
            render: ({ end_lounch }) =>
              end_lounch
                ? formatLocale(end_lounch, 'DD/MM/YYYY HH:mm:ss')
                : '--',
          },
          {
            accessor: 'finish',
            title: 'Saída',
            render: ({ finish }) =>
              finish ? formatLocale(finish, 'DD/MM/YYYY HH:mm:ss') : '--',
          },
          {
            accessor: 'workHours',
            title: 'Horas Trabalhadas',
            render: ({ workHours, type }) =>
              type === WorkType.WORK && workHours,
          },
          {
            accessor: 'combined1',
            title: 'Valor da Diária',
            render: ({ price, type }) =>
              type === WorkType.WORK && formatBRL(String(price)),
          },
          {
            accessor: 'totalValue',
            title: 'Valor a Receber',
            render: ({ totalValue, type }) =>
              type === WorkType.WORK && totalValue,
          },
          {
            accessor: 'isBankHour',
            title: 'Escala',
            render: ({ isBankHour, type }) =>
              type === WorkType.WORK && (
                <Badge color={isBankHour ? 'red' : 'blue'}>
                  {isBankHour ? 'Sim' : 'Nâo'}
                </Badge>
              ),
          },
          {
            accessor: 'description',
            title: 'Justificativa',
            render: ({ justification }) =>
              justification && dailyWorkJustificationHumanized?.[justification],
          },
          {
            accessor: 'description',
            title: 'Descrição',
            render: ({ description }) =>
              description && (
                <Tooltip maw={200} multiline label={description} withArrow>
                  <Button variant="subtle" p={0} children={<IconMessage />} />
                </Tooltip>
              ),
          },
          {
            accessor: 'location',
            title: 'Localização',
            render: ({ location }) =>
              location && (
                <Tooltip maw={200} multiline label={location} withArrow>
                  <Button variant="subtle" p={0} children={<IconMapPin />} />
                </Tooltip>
              ),
          },
          {
            accessor: 'daily-word-edit-menu',
            title: '',
            render: (dailyItem) => (
              <Menu disabled={isNotMaster}>
                <Menu.Target>
                  <Button color="blue" variant="subtle" w={40} p={0}>
                    <IconDotsVertical />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown style={{ position: 'absolute' }}>
                  <Menu.Item
                    onClick={() => handleViewFiles(dailyItem.id)}
                    icon={<IconFile size={14} />}
                  >
                    Arquivos
                  </Menu.Item>
                  <Menu.Item
                    /*
                    disabled={
                      user?.user.id !== 1082 &&
                      user?.user.id !== 1009 &&
                      user?.user.id !== 2849
                    }*/
                    onClick={() =>
                      dailyItem.type === WorkType.JUSTIFICATION
                        ? handleSelectJustification(dailyItem)
                        : handleSelectDailyWork(dailyItem)
                    }
                    icon={<IconEdit size={14} />}
                  >
                    Editar
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconMedicineSyrup size={14} />}
                    disabled={
                      dailyItem.type === WorkType.JUSTIFICATION ||
                      (dailyItem?.justificationTime ?? 0) > 0
                    }
                    onClick={() => handleSelectPartialJustification(dailyItem)}
                  >
                    Justificar
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconTrash size={14} />}
                    disabled={dailyItem.type !== WorkType.JUSTIFICATION}
                    onClick={() => handleRemoveJustification(dailyItem.id)}
                  >
                    Remover
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ),
          },
        ]}
      />
    ),
    [dailyWorkList, selectedDailyWorkList.length],
  );

  const selectedUserDailyListReceiptFiltered = useMemo(
    () =>
      receiptUserList.filter((user) =>
        selectedUsersToReceipt.some((id) => id === user.id),
      ),
    [receiptUserList, selectedUsersToReceipt],
  );

  const selectedUserVTListReceiptFiltered = useMemo(
    () =>
      etUserList.filter((user) =>
        selectedUsersToVT.some((selectedUser) => selectedUser.id === user.id),
      ),
    [etUserList, selectedUsersToVT],
  );

  /** Lists filtered by the debounced name input in the unified-receipt modal. */
  const filteredReceiptList = useMemo(
    () =>
      receiptUserList.filter((u) =>
        u.username?.toLowerCase().includes(debouncedUnifiedName.toLowerCase()),
      ),
    [receiptUserList, debouncedUnifiedName],
  );

  const filteredEtList = useMemo(
    () =>
      etUserList.filter((u) =>
        u.username?.toLowerCase().includes(debouncedUnifiedName.toLowerCase()),
      ),
    [etUserList, debouncedUnifiedName],
  );

  /** Paginated slice of filteredEtList for the VT DataTable. */
  const pagedEtList = useMemo(
    () =>
      filteredEtList.slice(
        (vtModalPage - 1) * vtModalPageSize,
        vtModalPage * vtModalPageSize,
      ),
    [filteredEtList, vtModalPage],
  );

  /** Pre-computed card data for the unified-receipt preview step. */
  const previewCards = useMemo(() => {
    if (!previewUnifiedData) return [];
    const usernames = Array.from(
      new Set([
        ...previewUnifiedData.dailyParams.map((p) => p.username),
        ...previewUnifiedData.transportParams.map((p) => p.username),
      ]),
    );
    return usernames.map((username) => {
      const dailyParam = previewUnifiedData.dailyParams.find(
        (p) => p.username === username,
      );
      const transportParam = previewUnifiedData.transportParams.find(
        (p) => p.username === username,
      );
      const totalDailyPriceVal =
        Number(realToUSCash(dailyParam?.totalPrice ?? '0')) || 0;
      const vtTotalVal =
        Number(realToUSCash(transportParam?.totalPrice ?? '0')) || 0;
      const adjustmentTotalVal =
        dailyParam?.values
          .filter((v) => v.type === 'daily-adjustment')
          .reduce((acc, v) => acc + (Number(realToUSCash(v.price)) || 0), 0) ??
        0;
      return {
        username,
        dailyParam,
        transportParam,
        mainDailyTotal: (totalDailyPriceVal - adjustmentTotalVal).toFixed(2),
        adjustmentTotal: adjustmentTotalVal.toFixed(2),
        vtTotal: vtTotalVal.toFixed(2),
        grandTotal: (totalDailyPriceVal + vtTotalVal).toFixed(2),
        dailyDates:
          dailyParam?.dates
            ?.map((d) => dayjsPlugins(d).format('DD/MM'))
            .join(', ') ?? '',
        vtDates:
          transportParam?.dates
            ?.map((d) => dayjsPlugins(d).format('DD/MM'))
            .join(', ') ?? '',
      };
    });
  }, [previewUnifiedData]);

  const pagedPreviewCards = useMemo(
    () =>
      previewCards.slice(
        (previewPage - 1) * previewPageSize,
        previewPage * previewPageSize,
      ),
    [previewCards, previewPage],
  );

  useEffect(() => {
    if (!selectedDates.length || !etUserList.length) return;

    setSelectedUsersToVT((prevUsers) => {
      const updatedUsers = prevUsers.map((item) => {
        const user = etUserList.find(({ id }) => id === item.id);
        return user
          ? {
              ...item,
              value: (
                Number(user.transportPrice) * selectedDates.length
              ).toFixed(2),
            }
          : item;
      });

      return JSON.stringify(updatedUsers) !== JSON.stringify(prevUsers)
        ? updatedUsers
        : prevUsers;
    });
  }, [selectedDates, etUserList]);

  useEffect(() => {
    if (
      !selectedUsersToReceipt.length ||
      !selectedReceiptOptions.includes('transport')
    )
      return;

    setSelectedUsersToVT(
      selectedUsersToReceipt.map((userId) => ({ id: userId, value: '' })),
    );
    setSelectedUsersToVT((prevUsers) => {
      const updatedUsers = prevUsers.map((item) => {
        const user = selectedUsersToReceipt.find((id) => id === item.id);
        return user
          ? {
              ...item,
              id: user,
              value: '',
            }
          : item;
      });

      return JSON.stringify(updatedUsers) !== JSON.stringify(prevUsers)
        ? updatedUsers
        : prevUsers;
    });
  }, [selectedUsersToReceipt]);

  useEffect(() => {
    if (
      pageModal === 'generate-receipt-unified-pdf' &&
      globalUnifiedRole &&
      receiptListForm.values.dateBegin &&
      receiptListForm.values.dateEnd
    ) {
      handleFilterReceiptList({
        ...receiptListForm.values,
        role: globalUnifiedRole as UserRole,
      });
      handleGetETUsers({ role: globalUnifiedRole as UserRole });
    }
  }, [
    pageModal,
    globalUnifiedRole,
    receiptListForm.values.dateBegin,
    receiptListForm.values.dateEnd,
  ]);

  useEffect(() => {
    getUsers(debouncedUserSearch);
  }, [debouncedUserSearch]);

  // Reset VT table page when the name filter changes to avoid showing empty pages.
  useEffect(() => {
    setVtModalPage(1);
  }, [debouncedUnifiedName]);

  return (
    <Page>
      <CustomLoader
        loading={
          pageLoading ||
          addDailyWorkManuallyLoader ||
          editDailyWorkLoader ||
          addDailyWorkJustificationLoader ||
          checkBankHoursToUseLoader ||
          removeJustificationLoader
        }
      />
      <Flex m={16} direction="column">
        <Flex align="center" justify="end">
          <Button
            mr={8}
            rightIcon={<IconTableExport size={18} />}
            disabled={isManagerViewer}
            onClick={() => setPageModal('generate-receipt-unified')}
          >
            Recibos
          </Button>
          <Button
            mr={8}
            rightIcon={<IconTableExport size={18} />}
            disabled={isManagerViewer}
            onClick={() => setPageModal('generate-receipt-unified-pdf')}
          >
            Recibo Unificado
          </Button>
          <Button
            mr={8}
            color="green"
            rightIcon={<IconDownload size={18} />}
            disabled={!dailyWorkList.length}
            onClick={() => exportDailyWorkToExcel(dailyWorkList)}
          >
            Exportar Excel
          </Button>
          {canRecalculate && (
            <Tooltip
              multiline
              maw={280}
              withArrow
              label="Recalcula as horas dos dias selecionados a partir das batidas, com a escala e as regras atuais. Mostra o efeito antes de aplicar."
            >
              <span>
                <Button
                  mr={8}
                  variant="outline"
                  color="ltpBlue.9"
                  disabled={!selectedDailyWorkList.length}
                  onClick={() => setRecalculating(true)}
                >
                  Recalcular
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            multiline
            maw={280}
            withArrow
            label="Marca os dias selecionados como escala. No sábado só conta como banco de horas o tempo depois das 13h; no domingo nada conta (o dia é pago em dinheiro)."
          >
            <span>
              <Button
                disabled={!selectedDailyWorkList.length}
                color="ltpBlue.9"
                rightIcon={<IconListCheck />}
                onClick={handleSubmitSetBankHour}
              >
                Escala
              </Button>
            </span>
          </Tooltip>
          <Menu
            disabled={user?.user.type !== UserType.MASTER}
            shadow="md"
            width={200}
          >
            <Menu.Target>
              <Button
                disabled={user?.user.type !== UserType.MASTER}
                color="teal.5"
                ml={8}
                type="submit"
                rightIcon={<IconListDetails />}
              >
                Adicionar
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Tipos de recibo</Menu.Label>
              <Menu.Item
                onClick={() => setPageModal('add-daily-work')}
                icon={<IconClock size={14} />}
              >
                Horário
              </Menu.Item>
              <Menu.Item
                onClick={() => setPageModal('add-work-justification')}
                icon={<IconMedicineSyrup size={14} />}
              >
                Justificativa
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
        <Flex wrap="wrap" justify="start" mb={8}>
          <form
            onSubmit={formFilter.onSubmit((values) => {
              handleFilter(values);
            })}
          >
            <Flex align="center">
              <Select
                searchable
                onSearchChange={setUserSearch}
                clearable
                label="Usuário"
                placeholder="selecione o usuário"
                data={userList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('userId')}
              />
              <Select
                clearable
                name="role"
                label="Tipo de usuário"
                placeholder="selecione o tipo e usuário"
                data={userRoleList}
                {...formFilter.getInputProps('role')}
                mb={16}
                mr={8}
              />
              <CustomDatePicker
                required
                withAsterisk
                placeholder="selecione uma data"
                label="Data Inicial"
                maw={170}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('dateBegin')}
              />
              <CustomDatePicker
                required
                withAsterisk
                placeholder="selecione uma data"
                label="Data Final"
                maw={170}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('dateEnd')}
              />
              <Button mb={-10} type="submit">
                Filtrar
              </Button>
            </Flex>
          </form>
        </Flex>
        {RenderDataTable}
        <Group mt={8} position="right">
          <Text fw="bold">Total:</Text>
          <Text>{formatBRL(pageTotal?.toFixed(2))}</Text>
        </Group>
      </Flex>
      <Modal
        title="Exportar Recibo."
        opened={pageModal === 'confirm-receipt'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group>
          <Text fw="bold">Tudo certo!</Text>
          <Text>O recibo vai aparecer em instantes no seus Downloads.</Text>
        </Group>
        <Group position="right">
          <Button onClick={() => handleCloseModal()}>OK</Button>
        </Group>
      </Modal>
      <Modal
        size="80%"
        title={<Text fw="bold">Recibo: Diárias</Text>}
        opened={pageModal === 'generate-receipt'}
        onClose={onCloseDailyReceiptModal}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Flex wrap="wrap" align="center" justify="start" mb={8}>
          <form
            onSubmit={receiptListForm.onSubmit((values) =>
              handleFilterReceiptList(values),
            )}
          >
            <Flex align="center">
              <CustomDatePicker
                required
                withAsterisk
                placeholder="selecione uma data"
                label="Data Inicial"
                maw={170}
                mb={16}
                mr={8}
                {...receiptListForm.getInputProps('dateBegin')}
              />
              <CustomDatePicker
                required
                withAsterisk
                placeholder="selecione uma data"
                label="Data Final"
                maw={170}
                mb={16}
                mr={8}
                {...receiptListForm.getInputProps('dateEnd')}
              />
              <Select
                required
                withAsterisk
                name="role"
                label="Tipo de usuário"
                placeholder="selecione o tipo e usuário"
                data={userRoleList}
                {...receiptListForm.getInputProps('role')}
                mb={16}
                mr={8}
              />
              <Button mb={-10} type="submit">
                Buscar
              </Button>
            </Flex>
          </form>
        </Flex>
        <DataTable
          selectedRecords={selectReceiptUser}
          onSelectedRecordsChange={setSelectReceiptUser}
          fetching={pageLoading}
          minHeight={200}
          height={300}
          noRecordsText="Sem usuários"
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={receiptUserList}
          columns={[
            {
              accessor: 'username',
              title: 'Funcionário',
            },
            {
              accessor: 'userRole',
              title: 'Tipo',
              render: ({ role }) => userRoleHumanized[role],
            },
            {
              accessor: 'transportPrice',
              title: 'Horas trabalhadas',
              render: ({ workMinutes }) => {
                const restMinutes = workMinutes % 60;
                const hours = (workMinutes - restMinutes) / 60;

                return `${hours}hr ${restMinutes}min`;
              },
            },
            {
              accessor: 'transportPrice',
              title: 'Valor Diária',
              render: ({ dailyPrice }) => formatBRL(dailyPrice?.toFixed(2)),
            },
            {
              accessor: 'transportPrice',
              title: 'Valor total',
              render: ({ totalPrice }) => formatBRL(totalPrice?.toFixed(2)),
            },
            {
              accessor: '',
              title: '',
              render: ({ isPaid }) =>
                isPaid && (
                  <ThemeIcon title="ja emitido" variant="light" color="green">
                    <IconCheck />
                  </ThemeIcon>
                ),
            },
          ]}
        />
        <Group mt={16} position="right">
          <Button
            disabled={!selectReceiptUser.length}
            onClick={() =>
              handleDailyWorkReceitpExport({
                dateBegin: receiptListForm.values.dateBegin,
                dateEnd: receiptListForm.values.dateEnd,
                role: receiptListForm.values.role,
              })
            }
          >
            Gerar Recibo
          </Button>
        </Group>
      </Modal>
      <Modal
        size="80%"
        title={<Text fw="bold">Recibo: Vale Transporte</Text>}
        opened={pageModal === 'generate-vt'}
        onClose={handleCloseVTModal}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Grid columns={5}>
          <Grid.Col span={2}>
            <Group position="center">
              <CustomCalendar
                value={selectedDates}
                onChange={(dates) => handleSelectDates(dates)}
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={3}>
            <Container display={selectedDates.length ? 'block' : 'none'}>
              <form
                onSubmit={transportListForm.onSubmit((values) =>
                  handleGetETUsers(values),
                )}
              >
                <Flex align="center">
                  <Select
                    required
                    withAsterisk
                    name="role"
                    label="Tipo de usuário"
                    placeholder="selecione o tipo e usuário"
                    data={userRoleList}
                    {...transportListForm.getInputProps('role')}
                    mb={16}
                    mr={8}
                  />
                  <Button mb={-10} type="submit">
                    Filtrar
                  </Button>
                </Flex>
              </form>
              <DataTable
                fetching={pageLoading}
                minHeight={200}
                height={300}
                noRecordsText="Sem usuários"
                withBorder
                borderRadius="sm"
                striped
                highlightOnHover
                records={etUserList}
                columns={[
                  {
                    accessor: 'username',
                    title: 'Funcionário',
                  },
                  {
                    accessor: 'userRole',
                    title: 'Tipo',
                    render: ({ userRole }) => userRoleHumanized[userRole],
                  },
                  {
                    accessor: 'transportPrice',
                    title: 'Valor VT',
                    render: ({ transportPrice }) => formatBRL(transportPrice),
                  },
                  {
                    accessor: 'transportPrice',
                    title: 'Valor total',
                    render: ({ transportPrice }) => {
                      const sum = Number(
                        Number(transportPrice) * selectedDates.length,
                      )?.toFixed(2);
                      return formatBRL(sum);
                    },
                  },
                  {
                    accessor: 'combined4',
                    title: '',
                    render: ({ id, transportPrice }) => (
                      <Checkbox
                        checked={selectedUsersToVT.some(
                          (item) => item.id === id,
                        )}
                        onChange={() =>
                          handleSelectUserToVT({
                            id,
                            value: Number(
                              Number(transportPrice) * selectedDates.length,
                            )?.toFixed(2),
                          })
                        }
                      />
                    ),
                  },
                  {
                    accessor: 'combined5',
                    title: '',
                    render: ({ transportCreatedAt }) =>
                      dayjsPlugins(transportCreatedAt).format('DD/MM/YYYY') ===
                        dayjsPlugins().format('DD/MM/YYYY') && (
                        <ThemeIcon
                          title="ja emitido"
                          variant="light"
                          color="green"
                        >
                          <IconCheck />
                        </ThemeIcon>
                      ),
                  },
                ]}
              />
              <Group mt={16} position="right">
                <Button onClick={handleEmployeeTransportReceitpExport}>
                  Gerar Recibo
                </Button>
              </Group>
            </Container>
          </Grid.Col>
        </Grid>
        <Text fw="bold">Instruções:</Text>
        <Text>
          1- selecione as datas e logo após dê um check nos usuários que deseja
          gerar o recibo.
        </Text>
        <Text>2- logo aṕos, o recibo será baixado.</Text>
      </Modal>
      <Modal
        size={360}
        title={<Text fw="bold">Editar Horário</Text>}
        opened={pageModal === 'edit-daily-work'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Text>Funcionário:</Text>
        <Text>{selectedDailyWork?.['username']}</Text>
        <form
          onSubmit={editDailyWorkForm.onSubmit((values) =>
            handleEditDailyWork(values),
          )}
        >
          <Grid columns={2} mb={16}>
            <Grid.Col span={2}>
              <DateFormInput
                form={editDailyWorkForm}
                formKey="begin"
                label="Entrada:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={editDailyWorkForm}
                formKey="start_lounch"
                label="Inicio Intervalo:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={editDailyWorkForm}
                formKey="end_lounch"
                label="Fim Intervalo:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={editDailyWorkForm}
                formKey="finish"
                label="Saída:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                label="Valor da diária"
                placeholder="digite o valor"
                type="text"
                value={editDailyWorkForm.values.price}
                onChange={(e) =>
                  editDailyWorkForm.setFieldValue(
                    'price',
                    formatBRL(e.target.value),
                  )
                }
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Textarea
                label="Observações"
                placeholder="obs sobre diária"
                {...editDailyWorkForm.getInputProps('description')}
              />
            </Grid.Col>
          </Grid>
          <Group position="right">
            <Button type="submit">Salvar</Button>
          </Group>
        </form>
      </Modal>
      <Modal
        size={360}
        title={<Text fw="bold">Adicionar Horário</Text>}
        opened={pageModal === 'add-daily-work'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form onSubmit={addDailyWorkForm.onSubmit(handleAddDailyWork)}>
          <Grid columns={2} mb={16}>
            <Grid.Col span={2}>
              <Select
                withAsterisk
                required
                searchable
                onSearchChange={setUserSearch}
                clearable
                label="Usuário"
                placeholder="selecione o usuário"
                data={userList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...addDailyWorkForm.getInputProps('user')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={addDailyWorkForm}
                formKey="begin"
                label="Entrada:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={addDailyWorkForm}
                formKey="start_lounch"
                label="Inicio Intervalo:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={addDailyWorkForm}
                formKey="end_lounch"
                label="Fim Intervalo:"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DateFormInput
                form={addDailyWorkForm}
                formKey="finish"
                label="Saída"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Textarea
                label="Observações"
                placeholder="obs sobre diária"
                {...addDailyWorkForm.getInputProps('description')}
              />
            </Grid.Col>
          </Grid>
          <Group position="right">
            <Button loading={addDailyWorkManuallyLoader} type="submit">
              Salvar
            </Button>
          </Group>
        </form>
      </Modal>
      <Modal
        size={660}
        title={<Text fw="bold">Adicionar Justificativa</Text>}
        opened={pageModal === 'add-work-justification'}
        onClose={() => {
          setPageModal(null);
          addDailyWorkJustificationForm.reset();
          setSelectedDates([]);
        }}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={addDailyWorkJustificationForm.onSubmit(
            addDailyWordJustification,
          )}
        >
          <Grid columns={2}>
            <Grid.Col span={2}>
              <Select
                withAsterisk
                required
                searchable
                clearable
                label="Usuário"
                placeholder="selecione o usuário"
                data={userList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mr={8}
                {...addDailyWorkJustificationForm.getInputProps('user')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <CustomCalendar
                value={selectedDates}
                onChange={(dates) => setSelectedDates(dates)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                required
                withAsterisk
                searchable
                clearable
                label="Justificativa"
                placeholder="comece a digitar.."
                data={workJustification}
                mr={8}
                {...addDailyWorkJustificationForm.getInputProps(
                  'justification',
                )}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size={'sm'}>Arquivo de justificativa</Text>
              <CustomMultipleFileButton
                name="files"
                accept="application/pdf,image/png,image/jpeg,image/jpg,.eps,.ai,.cdr,.svg,.ps,.bin"
                label="Arquivo de justificativa"
                placeholder="selecione arquivo por vez"
                formRef={addDailyWorkJustificationForm}
                textInput="Clique para anexar o arquivo"
                icon={<IconFileInfo size={24} />}
              />
            </Grid.Col>
          </Grid>
          <Group position="right" mt={'md'}>
            <Button
              loading={addDailyWorkManuallyLoader || uploadFilesLoader}
              type="submit"
            >
              Salvar
            </Button>
          </Group>
        </form>
      </Modal>
      <Modal
        size={360}
        title={<Text fw="bold">Editar Justificativa</Text>}
        opened={pageModal === 'edit-work-justification'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={editDailyWorkJustificationForm.onSubmit((values) =>
            editDailyWordJustification(values),
          )}
        >
          <Grid columns={2} mb={16}>
            <Grid.Col span={2}>
              <CustomDatePicker
                required
                withAsterisk
                placeholder="selecione uma data"
                label="Data"
                maw={170}
                mb={16}
                mr={8}
                {...editDailyWorkJustificationForm.getInputProps('begin')}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                required
                withAsterisk
                searchable
                clearable
                label="Justificativa"
                placeholder="comece a digitar.."
                data={workJustification}
                mb={16}
                mr={8}
                {...editDailyWorkJustificationForm.getInputProps(
                  'justification',
                )}
              />
            </Grid.Col>
          </Grid>
          <Group position="right">
            <Button loading={editDailyWorkJustificationLoader} type="submit">
              Salvar
            </Button>
          </Group>
        </form>
      </Modal>
      <Modal
        size={560}
        title={<Text fw="bold">Adicionar justificativa parcial</Text>}
        opened={pageModal === 'partial-justification'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Flex direction={'column'} justify={'center'} mb={'xl'} gap={'xl'}>
          <Flex>
            {!checkBankHoursToUseResponse?.[0].isAllowedToUse ? (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Atenção"
                color="yellow"
                mt={'lg'}
                mb={'lg'}
              >
                O funcionário não possui horas disponíveis que permitam a
                criação de uma justificativa parcial.
              </Alert>
            ) : (
              ''
            )}
          </Flex>
          <Flex justify={'space-around'} mb={16}>
            <Box>
              <Text color={'dimmed'} size={'sm'}>
                Funcionário:
              </Text>
              <Text fw={'bold'}>{selectedDailyWork?.username}</Text>
            </Box>

            <Box>
              <Text color={'dimmed'} size={'sm'}>
                Data:
              </Text>
              <Text fw={'bold'}>
                {selectedDailyWork?.begin
                  ? formatLocale(selectedDailyWork?.begin, 'DD/MM/YYYY')
                  : '--'}
              </Text>
            </Box>
          </Flex>

          <Flex gap={'lg'} align={'center'} justify={'space-evenly'}>
            <Text fw={'bold'}>
              {selectedDailyWork?.begin
                ? formatLocale(selectedDailyWork?.begin, 'HH:mm')
                : '--'}
            </Text>
            <Text fw={'bold'}>
              {selectedDailyWork?.start_lounch
                ? formatLocale(selectedDailyWork?.start_lounch, 'HH:mm')
                : '--'}
            </Text>
            <Text fw={'bold'}>
              {selectedDailyWork?.end_lounch
                ? formatLocale(selectedDailyWork?.end_lounch, 'HH:mm')
                : '--'}
            </Text>
            <Text fw={'bold'}>
              {selectedDailyWork?.finish
                ? formatLocale(selectedDailyWork?.finish, 'HH:mm')
                : '--'}
            </Text>
          </Flex>
        </Flex>

        <form
          onSubmit={parcialDailyWorkJustificationForm.onSubmit((values) =>
            handleAddPartialJustification(values),
          )}
        >
          <Grid columns={2} mb={16}>
            <Grid.Col span={2}>
              <Select
                required
                withAsterisk
                searchable
                clearable
                label="Justificativa"
                placeholder="comece a digitar.."
                data={workJustification}
                mb={16}
                mr={8}
                {...parcialDailyWorkJustificationForm.getInputProps(
                  'justification',
                )}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Flex align="center" gap={'xl'}>
                <TimeInput
                  label="Inicio"
                  withAsterisk
                  required
                  icon={<IconClock size={16} />}
                  clearable
                  {...parcialDailyWorkJustificationForm.getInputProps('start')}
                />
                <TimeInput
                  label="Fim"
                  withAsterisk
                  required
                  icon={<IconClock size={16} />}
                  {...parcialDailyWorkJustificationForm.getInputProps('end')}
                  clearable
                />
              </Flex>
            </Grid.Col>
            <Grid.Col span={2}>
              <Flex gap={'sm'}>
                <Text>Tempo justificado:</Text>
                <Text fw={'bold'}>
                  {formatHours(rangeTimeParcialJustification)}
                </Text>
              </Flex>
            </Grid.Col>
            <Grid.Col span={2}>
              <Flex gap={'sm'}>
                <Text>Banco de horas:</Text>
                <Text fw={'bold'}>
                  {checkBankHoursToUseResponse?.[0]?.totalAvailableTime ||
                    '00h 00min'}
                </Text>
              </Flex>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size={'sm'}>Arquivo de justificativa</Text>
              <CustomMultipleFileButton
                name="files"
                accept="application/pdf,image/png,image/jpeg,image/jpg,.eps,.ai,.cdr,.svg,.ps,.bin"
                label="Arquivo de justificativa"
                placeholder="selecione arquivo por vez"
                formRef={parcialDailyWorkJustificationForm}
                textInput="Clique para anexar o arquivo"
                icon={<IconFileInfo size={24} />}
              />
            </Grid.Col>
          </Grid>
          <Group position="right">
            <Button
              loading={addDailyWorkPartialJustificationLoader}
              type="submit"
            >
              Salvar
            </Button>
          </Group>
        </form>
      </Modal>
      <Modal
        size={'70%'}
        title={<Text fw="bold">Gerar recibos</Text>}
        opened={pageModal === 'generate-receipt-unified'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Flex direction={'column'}>
          <Stepper active={receiptStep} breakpoint="sm" size="sm">
            <Stepper.Step
              label="Tipo"
              //description="Selecione o tipo"
            >
              <Checkbox.Group
                orientation="vertical"
                size="md"
                label="Tipo"
                description="Selecione o tipo de recibo"
                withAsterisk
                required
                value={selectedReceiptOptions}
                onChange={setSelectedReceiptOptions}
              >
                <Checkbox label="Recibo de diária" value={'daily'} />
                <Checkbox
                  label="Recibo de vale transporte"
                  value={'transport'}
                />
              </Checkbox.Group>
            </Stepper.Step>

            {selectedReceiptOptions.includes('daily') && (
              <Stepper.Step label="Diária">
                <Flex wrap="wrap" align="center" justify="start" mb={8}>
                  <form
                    onSubmit={receiptListForm.onSubmit((values) =>
                      handleFilterReceiptList(values),
                    )}
                  >
                    <Flex align="center">
                      <CustomDatePicker
                        required
                        withAsterisk
                        placeholder="selecione uma data"
                        label="Data Inicial"
                        maw={170}
                        mb={16}
                        mr={8}
                        {...receiptListForm.getInputProps('dateBegin')}
                      />
                      <CustomDatePicker
                        required
                        withAsterisk
                        placeholder="selecione uma data"
                        label="Data Final"
                        maw={170}
                        mb={16}
                        mr={8}
                        {...receiptListForm.getInputProps('dateEnd')}
                      />
                      <Select
                        required
                        withAsterisk
                        name="role"
                        label="Tipo de usuário"
                        placeholder="selecione o tipo e usuário"
                        data={userRoleList}
                        {...receiptListForm.getInputProps('role')}
                        mb={16}
                        mr={8}
                      />
                      <Button mb={-10} type="submit">
                        Buscar
                      </Button>
                    </Flex>
                  </form>
                </Flex>
                <DataTable
                  fetching={pageLoading}
                  minHeight={200}
                  height={300}
                  noRecordsText="Sem usuários"
                  withBorder
                  borderRadius="sm"
                  striped
                  highlightOnHover
                  records={receiptUserList}
                  columns={[
                    {
                      accessor: 'username',
                      title: 'Funcionário',
                    },
                    {
                      accessor: 'userRole',
                      title: 'Tipo',
                      render: ({ role }) => userRoleHumanized[role],
                    },
                    {
                      accessor: 'transportPrice',
                      title: 'Horas trabalhadas',
                      render: ({ workMinutes }) => {
                        const restMinutes = workMinutes % 60;
                        const hours = (workMinutes - restMinutes) / 60;

                        return `${hours}hr ${restMinutes}min`;
                      },
                    },
                    {
                      accessor: 'transportPrice',
                      title: 'Valor Diária',
                      render: ({ dailyPrice }) =>
                        formatBRL(dailyPrice?.toFixed(2)),
                    },
                    {
                      accessor: 'transportPrice',
                      title: 'Valor total',
                      render: ({ totalPrice }) =>
                        formatBRL(totalPrice?.toFixed(2)),
                    },
                    {
                      accessor: '',
                      title: '',
                      render: ({ id }) => (
                        <Checkbox
                          checked={selectedUsersToReceipt.includes(id)}
                          onChange={() => handleSelectUserToReceipt(id)}
                        />
                      ),
                    },
                    {
                      accessor: '',
                      title: '',
                      render: ({ isPaid }) =>
                        isPaid && (
                          <ThemeIcon
                            title="ja emitido"
                            variant="light"
                            color="green"
                          >
                            <IconCheck />
                          </ThemeIcon>
                        ),
                    },
                  ]}
                />
              </Stepper.Step>
            )}

            {selectedReceiptOptions.includes('transport') && (
              <Stepper.Step label="Vale Transporte">
                <Grid columns={5}>
                  <Grid.Col span={2}>
                    <Group position="center">
                      <CustomCalendar
                        value={selectedDates}
                        onChange={(dates) => handleSelectDates(dates)}
                      />
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Container>
                      <form
                        onSubmit={transportListForm.onSubmit((values) =>
                          handleGetETUsers(values),
                        )}
                      >
                        <Flex align="center">
                          <Select
                            required
                            withAsterisk
                            name="role"
                            label="Tipo de usuário"
                            placeholder="selecione o tipo e usuário"
                            data={userRoleList}
                            {...transportListForm.getInputProps('role')}
                            mb={16}
                            mr={8}
                          />
                          <Button mb={-10} type="submit">
                            Filtrar
                          </Button>
                        </Flex>
                      </form>
                      <DataTable
                        fetching={pageLoading}
                        minHeight={200}
                        height={300}
                        noRecordsText="Sem usuários"
                        withBorder
                        borderRadius="sm"
                        striped
                        highlightOnHover
                        records={etUserList}
                        columns={[
                          {
                            accessor: 'username',
                            title: 'Funcionário',
                          },
                          {
                            accessor: 'userRole',
                            title: 'Tipo',
                            render: ({ userRole }) =>
                              userRoleHumanized[userRole],
                          },
                          {
                            accessor: 'transportPrice',
                            title: 'Valor VT',
                            render: ({ transportPrice }) =>
                              formatBRL(transportPrice),
                          },
                          {
                            accessor: 'transportPrice',
                            title: 'Valor total',
                            render: ({ transportPrice }) => {
                              const sum = Number(
                                Number(transportPrice) * selectedDates.length,
                              )?.toFixed(2);
                              return formatBRL(sum);
                            },
                          },
                          {
                            accessor: 'combined4',
                            title: '',
                            render: ({ id, transportPrice }) => (
                              <Checkbox
                                checked={selectedUsersToVT.some(
                                  (item) => item.id === id,
                                )}
                                onChange={() =>
                                  handleSelectUserToVT({
                                    id,
                                    value: Number(
                                      Number(transportPrice) *
                                        selectedDates.length,
                                    )?.toFixed(2),
                                  })
                                }
                              />
                            ),
                          },
                          {
                            accessor: 'combined5',
                            title: '',
                            render: ({ transportCreatedAt }) =>
                              dayjsPlugins(transportCreatedAt).format(
                                'DD/MM/YYYY',
                              ) === dayjsPlugins().format('DD/MM/YYYY') && (
                                <ThemeIcon
                                  title="ja emitido"
                                  variant="light"
                                  color="green"
                                >
                                  <IconCheck />
                                </ThemeIcon>
                              ),
                          },
                        ]}
                      />
                    </Container>
                  </Grid.Col>
                </Grid>
              </Stepper.Step>
            )}

            <Stepper.Completed>
              <Text mb={'xl'}>
                Revise os dados e clique em <b>gerar recibos.</b>
              </Text>
              <Grid columns={2}>
                <Grid.Col span={1}>
                  <Text fw={'bold'} color={'dimmed'} size={'sm'} mb={'sm'}>
                    Diárias
                  </Text>
                  {selectedUserDailyListReceiptFiltered.some(
                    (item) => item.isPaid,
                  ) ? (
                    <Text color={'red'} fw={'bold'} mb={'sm'}>
                      Opa! Algumas diárias selecionadas já foram pagas.
                    </Text>
                  ) : (
                    ''
                  )}
                  <DataTable
                    fetching={pageLoading}
                    minHeight={200}
                    height={300}
                    noRecordsText="Sem usuários"
                    withBorder
                    borderRadius="sm"
                    striped
                    highlightOnHover
                    records={selectedUserDailyListReceiptFiltered}
                    columns={[
                      {
                        accessor: 'username',
                        title: 'Funcionário',
                      },
                      {
                        accessor: 'userRole',
                        title: 'Tipo',
                        render: ({ role }) => userRoleHumanized[role],
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Valor Diária',
                        render: ({ dailyPrice }) =>
                          formatBRL(dailyPrice?.toFixed(2)),
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Valor total',
                        render: ({ totalPrice }) =>
                          formatBRL(totalPrice?.toFixed(2)),
                      },
                      {
                        accessor: '',
                        title: '',
                        render: ({ id }) => (
                          <Checkbox
                            checked={selectedUsersToReceipt.includes(id)}
                            onChange={() => handleSelectUserToReceipt(id)}
                          />
                        ),
                      },
                      {
                        accessor: '',
                        title: '',
                        render: ({ isPaid }) =>
                          isPaid && (
                            <ThemeIcon
                              title="ja emitido"
                              variant="light"
                              color="green"
                            >
                              <IconCheck />
                            </ThemeIcon>
                          ),
                      },
                    ]}
                  />
                </Grid.Col>

                <Grid.Col span={1}>
                  <Text fw={'bold'} color={'dimmed'} size={'sm'} mb={'sm'}>
                    Vale Transporte
                  </Text>
                  {selectedUserVTListReceiptFiltered.some(
                    (item) => item.transportCreatedAt,
                  ) ? (
                    <Text color={'red'} fw={'bold'} mb={'sm'}>
                      Opa! Alguns VT's selecionados já foram pagos.
                    </Text>
                  ) : (
                    ''
                  )}
                  <DataTable
                    fetching={pageLoading}
                    minHeight={200}
                    height={300}
                    noRecordsText="Sem usuários"
                    withBorder
                    borderRadius="sm"
                    striped
                    highlightOnHover
                    records={selectedUserVTListReceiptFiltered}
                    columns={[
                      {
                        accessor: 'username',
                        title: 'Funcionário',
                      },
                      {
                        accessor: 'userRole',
                        title: 'Tipo',
                        render: ({ userRole }) => userRoleHumanized[userRole],
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Valor VT',
                        render: ({ transportPrice }) =>
                          formatBRL(transportPrice),
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Valor total',
                        render: ({ transportPrice }) => {
                          const sum = Number(
                            Number(transportPrice) * selectedDates.length,
                          )?.toFixed(2);
                          return formatBRL(sum);
                        },
                      },
                      {
                        accessor: 'combined4',
                        title: '',
                        render: ({ id, transportPrice }) => (
                          <Checkbox
                            checked={selectedUsersToVT.some(
                              (item) => item.id === id,
                            )}
                            onChange={() =>
                              handleSelectUserToVT({
                                id,
                                value: Number(
                                  Number(transportPrice) * selectedDates.length,
                                )?.toFixed(2),
                              })
                            }
                          />
                        ),
                      },
                      {
                        accessor: 'combined5',
                        title: '',
                        render: ({ transportCreatedAt }) =>
                          dayjsPlugins(transportCreatedAt).format(
                            'DD/MM/YYYY',
                          ) === dayjsPlugins().format('DD/MM/YYYY') && (
                            <ThemeIcon
                              title="ja emitido"
                              variant="light"
                              color="green"
                            >
                              <IconCheck />
                            </ThemeIcon>
                          ),
                      },
                    ]}
                  />
                </Grid.Col>
              </Grid>
            </Stepper.Completed>
          </Stepper>

          <Group position="right" mt="xl">
            {receiptStep > 0 && (
              <Button variant="default" onClick={prevStep}>
                Anterior
              </Button>
            )}
            {receiptStep !== maxSteps && (
              <Button
                onClick={nextStep}
                disabled={!selectedReceiptOptions.length}
              >
                Próximo
              </Button>
            )}

            {receiptStep === maxSteps && (
              <Button
                onClick={() => generateReceiptsUnified()}
                disabled={
                  !selectedUsersToReceipt.length && !selectedUsersToVT.length
                }
                loading={
                  generateDailyWorkReceiptLoader ||
                  generateEmployeeTransportReceiptLoader
                }
              >
                Gerar recibos
              </Button>
            )}
          </Group>
        </Flex>
      </Modal>
      <Modal
        size={'90%'}
        title={
          <Group>
            <IconTableExport color="gray" />
            <Text fw="bold" size="lg">
              {unifiedReceiptStep === 'preview'
                ? 'Revisão dos Dados'
                : 'Gerar Recibo Unificado'}
            </Text>
          </Group>
        }
        opened={pageModal === 'generate-receipt-unified-pdf'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        {/* ── STEP 1: SELECTION ── */}
        {unifiedReceiptStep === 'selection' && (
          <>
            <Paper withBorder p="md" radius="md" mb="lg" bg="gray.0">
              <Grid align="flex-end">
                <Grid.Col span={6}>
                  <Select
                    name="role"
                    label="Tipo de usuário (Filtro Global)"
                    placeholder="Selecione o tipo"
                    data={userRoleList}
                    value={globalUnifiedRole}
                    onChange={(val) => setGlobalUnifiedRole(val)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Buscar funcionário (Filtro Global)"
                    placeholder="Digite o nome..."
                    value={globalUnifiedName}
                    onChange={(e) =>
                      setGlobalUnifiedName(e.currentTarget.value)
                    }
                  />
                </Grid.Col>
              </Grid>
            </Paper>

            <Grid gutter="xl">
              <Grid.Col span={6}>
                <Card withBorder radius="md" shadow="sm">
                  <Text fw={600} mb="sm" size="lg" color="dimmed">
                    1. Seleção de Diárias
                  </Text>

                  <form
                    onSubmit={receiptListForm.onSubmit((values) =>
                      handleFilterReceiptList({
                        ...values,
                        role: (globalUnifiedRole as UserRole) || undefined,
                      }),
                    )}
                  >
                    <Grid align="flex-end" mb="md">
                      <Grid.Col span={6}>
                        <CustomDatePicker
                          required
                          withAsterisk
                          placeholder="selecione uma data"
                          label="Data Inicial"
                          {...receiptListForm.getInputProps('dateBegin')}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <CustomDatePicker
                          required
                          withAsterisk
                          placeholder="selecione uma data"
                          label="Data Final"
                          {...receiptListForm.getInputProps('dateEnd')}
                        />
                      </Grid.Col>
                    </Grid>
                  </form>

                  <DataTable
                    fetching={pageLoading}
                    minHeight={200}
                    height={350}
                    noRecordsText="Sem usuários"
                    withBorder
                    borderRadius="sm"
                    striped
                    highlightOnHover
                    records={filteredReceiptList}
                    columns={[
                      {
                        accessor: 'username',
                        title: 'Funcionário',
                      },
                      {
                        accessor: 'userRole',
                        title: 'Tipo',
                        render: ({ role }) => userRoleHumanized[role],
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Total Diária',
                        render: ({ totalPrice }) =>
                          formatBRL(totalPrice?.toFixed(2)),
                      },
                      {
                        accessor: '',
                        title: '',
                        render: ({ id }) => (
                          <Checkbox
                            checked={selectedUsersToReceipt.includes(id)}
                            onChange={() => handleSelectUserToReceipt(id)}
                          />
                        ),
                      },
                      {
                        accessor: '',
                        title: '',
                        render: ({ isPaid }) =>
                          isPaid && (
                            <ThemeIcon
                              title="já emitido"
                              variant="light"
                              color="green"
                            >
                              <IconCheck />
                            </ThemeIcon>
                          ),
                      },
                    ]}
                  />
                </Card>
              </Grid.Col>

              <Grid.Col span={6}>
                <Card withBorder radius="md" shadow="sm">
                  <Flex justify="space-between" align="center" mb="sm">
                    <Text fw={600} size="lg" color="dimmed">
                      2. Seleção de Vale Transporte
                    </Text>
                  </Flex>

                  <Popover width={320} position="bottom" withArrow shadow="md">
                    <Popover.Target>
                      <TextInput
                        label="Dias de VT (Obrigatório):"
                        placeholder="Selecione as datas..."
                        value={selectedDates
                          .map((d) => dayjsPlugins(d).format('DD/MM'))
                          .join(', ')}
                        readOnly
                        mb="md"
                        styles={{ input: { cursor: 'pointer' } }}
                      />
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Group position="center">
                        <CustomCalendar
                          value={selectedDates}
                          onChange={(dates) => handleSelectDates(dates)}
                        />
                      </Group>
                    </Popover.Dropdown>
                  </Popover>

                  <DataTable
                    fetching={pageLoading}
                    minHeight={200}
                    height={350}
                    noRecordsText="Sem usuários"
                    withBorder
                    borderRadius="sm"
                    striped
                    highlightOnHover
                    // Paginação client-side: evita renderizar centenas de linhas de uma vez
                    page={vtModalPage}
                    onPageChange={(p) => setVtModalPage(p)}
                    recordsPerPage={vtModalPageSize}
                    totalRecords={filteredEtList.length}
                    records={pagedEtList}
                    columns={[
                      {
                        accessor: 'username',
                        title: 'Funcionário',
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Valor VT',
                        render: ({ transportPrice }) =>
                          formatBRL(transportPrice),
                      },
                      {
                        accessor: 'transportPrice',
                        title: 'Total VT',
                        render: ({ transportPrice }) => {
                          const sum = Number(
                            Number(transportPrice) * selectedDates.length,
                          )?.toFixed(2);
                          return formatBRL(sum);
                        },
                      },
                      {
                        accessor: 'combined4',
                        title: '',
                        render: ({ id, transportPrice }) => (
                          <Checkbox
                            checked={selectedUsersToVT.some(
                              (item) => item.id === id,
                            )}
                            onChange={() =>
                              handleSelectUserToVT({
                                id,
                                value: Number(
                                  Number(transportPrice) * selectedDates.length,
                                )?.toFixed(2),
                              })
                            }
                          />
                        ),
                      },
                      {
                        accessor: 'combined5',
                        title: '',
                        render: ({ transportCreatedAt }) =>
                          dayjsPlugins(transportCreatedAt).format(
                            'DD/MM/YYYY',
                          ) === dayjsPlugins().format('DD/MM/YYYY') && (
                            <ThemeIcon
                              title="já emitido"
                              variant="light"
                              color="green"
                            >
                              <IconCheck />
                            </ThemeIcon>
                          ),
                      },
                    ]}
                  />
                </Card>
              </Grid.Col>
            </Grid>

            <Group position="right" mt="xl">
              <Button
                size="md"
                onClick={handlePreviewUnifiedReceipt}
                disabled={
                  !selectedUsersToReceipt.length && !selectedUsersToVT.length
                }
                loading={pageLoading || previewUnifiedReceiptLoader}
                leftIcon={<IconFileInfo size={20} />}
              >
                Revisar Dados
              </Button>
            </Group>
          </>
        )}

        {/* ── STEP 2: PREVIEW ── */}
        {unifiedReceiptStep === 'preview' && previewUnifiedData && (
          <>
            <ScrollArea h={560} offsetScrollbars>
              <Grid columns={3} gutter="md">
                {pagedPreviewCards.map(
                  ({
                    username,
                    dailyParam,
                    transportParam,
                    mainDailyTotal,
                    adjustmentTotal,
                    vtTotal,
                    grandTotal,
                    dailyDates,
                    vtDates,
                  }) => (
                    <Grid.Col key={username} span={1}>
                      <Card withBorder radius="md" shadow="xs" p="md" h="100%">
                        <Text
                          fw={700}
                          size="sm"
                          mb="sm"
                          pb="xs"
                          style={{ borderBottom: '1px solid #e9ecef' }}
                          lineClamp={1}
                          title={username}
                        >
                          {username}
                        </Text>

                        <Flex direction="column" gap={8}>
                          {dailyParam && (
                            <Box>
                              <Flex justify="space-between" align="center">
                                <Text size="xs" color="dimmed">
                                  Diárias
                                </Text>
                                <Text size="xs" fw={600}>
                                  {formatBRL(mainDailyTotal)}
                                </Text>
                              </Flex>
                              {dailyDates && (
                                <Text
                                  size="xs"
                                  color="dimmed"
                                  mt={2}
                                  style={{ wordBreak: 'break-word' }}
                                >
                                  {dailyDates}
                                </Text>
                              )}
                            </Box>
                          )}

                          {Number(adjustmentTotal) !== 0 && (
                            <Flex justify="space-between" align="center">
                              <Text size="xs" color="dimmed">
                                Diferenças
                              </Text>
                              <Text size="xs" fw={600} color="orange">
                                {formatBRL(adjustmentTotal)}
                              </Text>
                            </Flex>
                          )}

                          {transportParam && (
                            <Box>
                              <Flex justify="space-between" align="center">
                                <Text size="xs" color="dimmed">
                                  Vale Transporte
                                </Text>
                                <Flex align="center" gap={4}>
                                  {transportParam.isTotalPriceNegative && (
                                    <Text
                                      size="xs"
                                      color="orange"
                                      title="Valor negativo"
                                    >
                                      ⚠
                                    </Text>
                                  )}
                                  <Text size="xs" fw={600}>
                                    {formatBRL(vtTotal)}
                                  </Text>
                                </Flex>
                              </Flex>
                              {vtDates && (
                                <Text
                                  size="xs"
                                  color="dimmed"
                                  mt={2}
                                  style={{ wordBreak: 'break-word' }}
                                >
                                  {vtDates}
                                </Text>
                              )}
                            </Box>
                          )}
                        </Flex>

                        <Divider my="sm" />
                        <Flex justify="space-between" align="center">
                          <Text size="sm" fw={700}>
                            Total
                          </Text>
                          <Text size="sm" fw={700} color="blue">
                            {formatBRL(grandTotal)}
                          </Text>
                        </Flex>
                      </Card>
                    </Grid.Col>
                  ),
                )}
              </Grid>
            </ScrollArea>
            {previewCards.length > previewPageSize && (
              <Group position="center" mt="md">
                <Button
                  variant="default"
                  size="xs"
                  disabled={previewPage === 1}
                  onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                >
                  ←
                </Button>
                <Text size="sm">
                  {previewPage} /{' '}
                  {Math.ceil(previewCards.length / previewPageSize)}
                </Text>
                <Button
                  variant="default"
                  size="xs"
                  disabled={
                    previewPage >=
                    Math.ceil(previewCards.length / previewPageSize)
                  }
                  onClick={() =>
                    setPreviewPage((p) =>
                      Math.min(
                        Math.ceil(previewCards.length / previewPageSize),
                        p + 1,
                      ),
                    )
                  }
                >
                  →
                </Button>
              </Group>
            )}

            <Group position="apart" mt="xl">
              <Button
                variant="default"
                onClick={() => setUnifiedReceiptStep('selection')}
              >
                ← Voltar
              </Button>
              <Button
                size="md"
                leftIcon={<IconDownload size={20} />}
                loading={generateUnifiedReceiptLoader}
                onClick={handleGenerateUnifiedPDF}
              >
                Gerar PDF
              </Button>
            </Group>
          </>
        )}
      </Modal>
      <Modal
        title="Confirmar"
        opened={modalConfirmation === 'confirm-create'}
        onClose={() => setPageModalConfirmation(null)}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Text fz="sm" mb={16}>
          Deseja realmente criar esta justificativa?
        </Text>
        <Button
          color="red"
          onClick={() => setPageModalConfirmation(null)}
          mr={16}
        >
          Voltar
        </Button>
        <Button
          color="green"
          onClick={() =>
            handleAddPartialJustification(
              parcialDailyWorkJustificationForm.values,
              true,
            )
          }
        >
          Confirmar
        </Button>
      </Modal>
      <Modal
        size={560}
        title="Arquivos"
        opened={pageModal === 'view-files'}
        onClose={() => setPageModal(null)}
      >
        {getDailyWorkFilesLoader ? (
          <Skeleton height={120} radius="md" mt={16} mb={16} />
        ) : getDailyWorkFilesResponse?.length ? (
          getDailyWorkFilesResponse?.map((file) => <CardFile item={file} />)
        ) : (
          <Flex align={'center'} justify={'center'} mt={'xl'}>
            <Text fw={'bold'}>Sem arquivos</Text>
          </Flex>
        )}
      </Modal>
      <RecalculateModal
        opened={recalculating}
        ids={selectedDailyWorkList.map(({ id }) => id)}
        onClose={() => setRecalculating(false)}
        onApplied={() => {
          setSelectedDailyWorkList([]);
          handleFilter(formFilter.values);
        }}
      />
    </Page>
  );
}
